"use client";

import { cn } from "@/lib/utils";
import { CaretDownIcon, CheckIcon } from "@phosphor-icons/react";

/**
 * v2 상품 선택 리스트박스 (07-17 확정 문법) — 타임슬롯 v2와 일일대여 v2 공용.
 *
 * ⚠️ 이 컴포넌트는 `BookingCard`의 `renderV2ListBox` 클로저를 **DOM 출력 그대로**
 * 승격한 것이다(08-17, 일일대여 v2 Phase 3). 마크업을 바꾸면 타임슬롯 v2가
 * 함께 회귀한다 — 클래스/구조 변경 금지, 확장은 props로만.
 *
 * 문법: rounded-xl 박스 + sunken 배경 + divide-y 행 + 사각 체크 + 잔여/매진 배지 +
 * 내부 스크롤·하단 페이드(v1 슬롯 리스트 관례 미러).
 *
 * ⚠️ 행 우측에 **가격을 표시하지 않는다**(08-20 확정 —
 * `docs/preview-bookingcard-price-4modes.html`). 우측 슬롯에 남는 것은
 * 매진 라벨과 잔여 1~3 배지뿐이고, 금액은 상세 본문 가격 안내와 하단
 * "선택 내역"에서만 노출된다. 판매/선택 판정에 쓰는 단가(0원 미노출 필터 등)는
 * **표시와 별개**이므로 호출측에 그대로 남아 있다.
 */

/** 리스트박스 1행 */
export type V2Row = {
  key: string;
  main: string;
  sub?: string;
  selected: boolean;
  soldOut: boolean;
  remaining?: number;
  onSelect: () => void;
};

/** 이 개수를 넘으면 리스트에 고정 높이 + 내부 스크롤을 적용한다. */
export const V2_LIST_SCROLL_THRESHOLD = 5;

interface V2ListBoxProps {
  rows: V2Row[];
  /** 컨텍스트(지점/날짜) 미완성 등으로 전체 비활성 */
  disabled: boolean;
  variant: "desktop" | "mobile";
  scrollRef: React.RefObject<HTMLDivElement | null>;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  /** 내부 스크롤이 바닥에 닿았는지 (하단 페이드 표시 여부) */
  atBottom: boolean;
  soldOutLabel: string;
  remainingLabel: (count: number) => string;
}

export default function V2ListBox({
  rows,
  disabled,
  variant,
  scrollRef,
  onScroll,
  atBottom,
  soldOutLabel,
  remainingLabel,
}: V2ListBoxProps) {
  const scrollable = rows.length > V2_LIST_SCROLL_THRESHOLD;
  return (
    <div
      className={cn(
        // v2 리스트 박스만 rounded-xl (07-17 사용자 지시 — v1 슬롯 박스는 rounded-md 불변)
        "relative rounded-xl border overflow-hidden bg-gray-50",
        disabled ? "border-gray-200" : "border-gray-300",
      )}
    >
      <div
        ref={scrollRef}
        onScroll={scrollable ? onScroll : undefined}
        className={cn(
          "divide-y divide-gray-200",
          scrollable &&
            "overflow-y-auto overscroll-contain " +
              (variant === "mobile" ? "max-h-60" : "max-h-72"),
        )}
      >
        {rows.map((row) => {
          const rowDisabled = disabled || row.soldOut;
          const showRemainingBadge =
            !rowDisabled &&
            row.remaining !== undefined &&
            row.remaining >= 1 &&
            row.remaining <= 3;
          return (
            <button
              key={row.key}
              type="button"
              disabled={rowDisabled}
              className={cn(
                "flex items-center justify-between w-full py-2.5 px-3 text-left text-sm transition-colors gap-3",
                rowDisabled
                  ? "opacity-50 cursor-not-allowed"
                  : row.selected
                  ? "bg-orange-50 cursor-pointer"
                  : "hover:bg-gray-100 cursor-pointer",
              )}
              onClick={() => {
                if (row.soldOut) return;
                row.onSelect();
              }}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span
                  className={cn(
                    "flex items-center justify-center w-5 h-5 rounded border shrink-0 transition-colors",
                    row.selected
                      ? "bg-orange-500 border-orange-500"
                      : "bg-white border-gray-300",
                  )}
                >
                  {row.selected && (
                    <CheckIcon className="w-3.5 h-3.5 text-white" weight="bold" />
                  )}
                </span>
                <div className="min-w-0">
                  <span
                    className={cn(
                      "block truncate font-medium",
                      row.selected ? "text-orange-700" : "text-gray-900",
                    )}
                  >
                    {row.main}
                  </span>
                  {row.sub && (
                    <span className="block truncate text-xs text-gray-500">
                      {row.sub}
                    </span>
                  )}
                </div>
              </div>
              {row.soldOut ? (
                <span className="whitespace-nowrap shrink-0 text-xs font-medium text-red-500">
                  {soldOutLabel}
                </span>
              ) : (
                /* 가격 미표시(08-20) — 우측 슬롯에는 잔여 배지만 남는다. */
                <span className="flex items-center gap-2 shrink-0">
                  {showRemainingBadge && (
                    <span className="whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                      {remainingLabel(row.remaining!)}
                    </span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* 내부스크롤 안내: 하단 페이드 + ↓ (v1 그대로) */}
      {scrollable && !atBottom && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-9 flex items-end justify-center bg-linear-to-t from-gray-100 to-transparent pb-0.5">
          <CaretDownIcon className="h-4 w-4 text-gray-500 animate-bounce" />
        </div>
      )}
    </div>
  );
}
